import { FormControl, FormLabel, Text, FormErrorMessage } from "@chakra-ui/react";
import { Select } from "chakra-react-select";

export interface SelectOption {
  value: any;
  label: string;
}

interface BaseSelectProps {
  fieldTitle?: string;
  requiredField?: boolean;
  options: SelectOption[];
  placeholder?: string;
  name?: string;
  value?: any;
  onChange?: (value: any) => void;
  errorText?: string;
  isEmpty?: boolean;
  containerStyles?: React.CSSProperties;
  isDisabled?: boolean;
  size?: "sm" | "md" | "lg";
  isSearchable?: boolean;
  isMulti?: boolean;
}

export const BaseSelect = ({
  fieldTitle,
  requiredField = false,
  options,
  placeholder = "Выберите опцию",
  name,
  value,
  onChange,
  errorText,
  isEmpty,
  containerStyles,
  isDisabled = false,
  size = "md",
  isSearchable = true,
  isMulti = false,
  ...selectProps
}: BaseSelectProps) => {
  const hasError = !!errorText;

  const selectedOption = isMulti
    ? Array.isArray(value)
      ? options.filter((option) => value.includes(option.value))
      : []
    : options.find((option) => option.value === value);

  return (
    <FormControl isInvalid={hasError} style={containerStyles}>
      {fieldTitle && (
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" display="flex" alignItems="center" gap={1} mb={2}>
          {fieldTitle}
          {requiredField && (
            <Text as="span" color="red">
              *
            </Text>
          )}
        </FormLabel>
      )}
      <Select
        name={name}
        value={selectedOption as any}
        onChange={(selected: any) => {
          if (isMulti) {
            const values = Array.isArray(selected) ? selected.map((opt: SelectOption) => opt.value) : [];
            onChange?.(values);
          } else {
            onChange?.((selected as SelectOption | null)?.value || null);
          }
        }}
        options={options}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        isClearable={true}
        isMulti={isMulti}
        size={size}
        chakraStyles={{
          control: (provided) => ({
            ...provided,
            borderColor: hasError ? "red.500" : "gray.200",
            _hover: {
              borderColor: hasError ? "red.500" : "blue.400",
            },
            _focus: {
              borderColor: hasError ? "red.500" : "blue.500",
              boxShadow: hasError ? "0 0 0 1px red.500" : "0 0 0 2px rgba(66, 153, 225, 0.2)",
            },
          }),
          placeholder: (provided) => ({
            ...provided,
            color: "gray.400",
            fontSize: "sm",
          }),
          singleValue: (provided) => ({
            ...provided,
            fontSize: "sm",
            color: "gray.700",
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            backgroundColor: "transparent",
            color: "gray.500",
            _hover: {
              backgroundColor: "transparent",
              color: "gray.700",
            },
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: "gray.200",
          }),
          option: (provided, state) => ({
            ...provided,
            fontSize: "sm",
            backgroundColor: state.isSelected ? "blue.500" : state.isFocused ? "blue.50" : "white",
            color: state.isSelected ? "white" : "gray.700",
            _hover: {
              backgroundColor: state.isSelected ? "blue.500" : "blue.50",
            },
          }),
        }}
        {...selectProps}
      />
      {errorText && <FormErrorMessage fontSize="xs">{errorText}</FormErrorMessage>}
    </FormControl>
  );
};
